import {useCallback} from 'react';
import {useHistory} from 'react-router-dom';

import {ALL_DATASETS_OPTION} from '../components/CompareEvaluationsDropdownButton';
import {useSaveCurrentUrl, useWeaveflowRouteContext} from '../context';
import {LeaderboardValueRecord} from '../views/Leaderboard/query/leaderboardQuery';
import {DatasetInfo} from './useLeaderboardComparison';

/**
 * Hook to handle navigation from leaderboard to comparison view
 * Implements consistent logic for filtering evaluations by dataset
 */
export const useLeaderboardNavigation = (
  entity: string,
  project: string,
  selectedEvaluations: string[],
  availableDatasets: DatasetInfo[],
  allRecords: LeaderboardValueRecord[]
) => {
  const history = useHistory();
  const {baseRouter} = useWeaveflowRouteContext();
  const saveCurrentUrl = useSaveCurrentUrl();

  const navigateToComparison = useCallback(
    (datasetFullName: string) => {
      // Save current URL before navigating
      saveCurrentUrl();

      // Handle "Compare all evaluations" option
      if (datasetFullName === ALL_DATASETS_OPTION) {
        history.push(
          baseRouter.compareEvaluationsUri(
            entity,
            project,
            selectedEvaluations,
            null,
            null
          )
        );
        return;
      }

      // For single evaluation, we can skip the complex filtering and just use the selected evaluation
      if (selectedEvaluations.length === 1) {
        history.push(
          baseRouter.compareEvaluationsUri(
            entity,
            project,
            selectedEvaluations,
            null,
            null
          )
        );
        return;
      }

      // For multiple evaluations, filter to only include those for the selected dataset+version
      // Parse the dataset name and version from the full name
      const selectedDataset = availableDatasets.find(
        d => d.name === datasetFullName
      );
      if (!selectedDataset) return;

      const uniqueFilteredEvaluations = [
        ...new Set(
          allRecords
            .filter(
              record =>
                record.sourceEvaluationCallId &&
                selectedEvaluations.includes(record.sourceEvaluationCallId) &&
                record.datasetName === selectedDataset.datasetName &&
                record.datasetVersion === selectedDataset.datasetVersion
            )
            .map(record => record.sourceEvaluationCallId)
        ),
      ];

      if (uniqueFilteredEvaluations.length > 0) {
        history.push(
          baseRouter.compareEvaluationsUri(
            entity,
            project,
            uniqueFilteredEvaluations,
            null,
            null
          )
        );
      }
    },
    [
      entity,
      project,
      selectedEvaluations,
      availableDatasets,
      allRecords,
      history,
      baseRouter,
      saveCurrentUrl,
    ]
  );

  return {navigateToComparison};
};
